import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {HTTP_STATUS_OK, SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import PageContentWrapper from '../../layout/PageContentWrapper'
import {getPagePathways} from '../../pathway/PathwayEndPoint'
import {
    usePagePathways,
    usePathwayCRUD,
} from '../../pathway/query-keys/PathwayKeysManager'
import StyledPathwayForm from '../../pathway/StyledPathwayForm'
import {useRoleContext} from '../../role/RoleProvider'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../broadcast/FilterEvent'
import {exportColumnTypes, exportRecordTypes, exportTypes} from '../Constant'
import StyledCardListWrapper from '../StyledCardListWrapper'
import StyledDeleteDialog from '../StyledDeleteDialog'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'
import {
    getPathwayEventKey,
    onPathwayTableChangeRemoveListener,
    pathwayEvent,
    pathwayEventTypes,
} from './broadcast/PathwayEvent'

import {ReactComponent as DuoPen} from '../../../assets/icons/DuoPen.svg'

const StyledPathwayDataCardList = () => {
    useSignals()

    const queryClient = useQueryClient()
    const {translateCommons} = TranslateUtils()

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {pathwayManagementPermissions} = useRoleContext()
    const {handleOnExportDatatable} = useImportNotificationContext()
    //#endregion

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.pathway,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(tableTypes.pathway)
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
        ...initialAdvancedFilterRequest,
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: null,
        }
    }, [])

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
        }
    }, [])

    // Table Action CRUD
    const formRef = useRef(null)
    const deleteRef = useRef(null)

    const {mutateAsync: deletePathway} = usePathwayCRUD(
        QueryCRUDTypes.delete,
        user?.id,
    )

    const handleAddNew = useCallback(() => {
        formRef.current?.showForm()
    }, [])

    const handleOnDelete = async data => {
        if (!!data && !!data?.id) {
            const response = await deletePathway(data)

            if (response === HTTP_STATUS_OK) {
                const {pathwayKeys} = QueryKeyManager(
                    QueryKeyTypes.pathway,
                    user?.id,
                )

                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries(pathwayKeys.pattern())
            }
        }
    }

    const handleAction = item => {
        formRef.current?.showForm({...item})
    }
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            if (
                userRole === SystemRole.ADMIN ||
                pathwayManagementPermissions?.includes(key)
            ) {
                return true
            }

            return false
        },
        [pathwayManagementPermissions, userRole],
    )
    //#endregion

    //#region Data Controls
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {pathwayKeys} = QueryKeyManager(QueryKeyTypes.pathway, user?.id)

        return pathwayKeys.page({
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: queryData, isFetched} = usePagePathways(
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )

    const dataList = useRef([])
    const currentDataList = useMemo(() => {
        dataList.current = queryData?.content || []

        return queryData?.content || []
    }, [queryData?.content])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getPagePathways(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    const handleOnDetailRowClick = id => {
        if (!!id) {
            const selectedRowData = dataList.current?.find(row => row.id === id)
            if (!!selectedRowData) {
                formRef.current?.showForm(selectedRowData)
            }
        }
    }

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getPagePathways(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])
    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        // TABLE CHANGE
        pathwayEvent.on(
            `${getPathwayEventKey(pathwayEventTypes.TABLE_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                const {pathwayKeys} = QueryKeyManager(
                    QueryKeyTypes.pathway,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: pathwayKeys.pattern(),
                })
            },
        )

        // PAGE
        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        // FILTER
        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.pathway,
                filterEventTypes.advanced,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            removeFilterChangeListener(
                tableTypes.pathway,
                filterEventTypes.advanced,
            )
            onPathwayTableChangeRemoveListener()
        }
    }, [])
    //#endregion

    //#region Export Data
    const handleRetrieveExportData = async (
        {
            exportType = exportTypes.excel, // csv, excel, pdf
            exportRecordType = exportRecordTypes.allRecords, // all, currentInView
            exportColumns = exportColumnTypes.allColumns, // all, currentInView
        },
        columns,
    ) => {
        handleOnExportDatatable(
            {
                exportType: exportType,
                exportRecordType: exportRecordType,
                exportColumns: exportColumns,
            },
            queryData,
            pageable.value,
            filterRequest.value,
            columns,
            tableTypes.pathway,
        )
    }
    //#endregion

    //#region  template
    const template = item => {
        return (
            <div className="items-center justify-center mb-2 w-full h-full">
                <div className="flex justify-content-between">
                    {' '}
                    <div>
                        {' '}
                        <div className="mb-2 font-bold text-xl text-blue-600">
                            {item?.name ? item.name : 'N/a'}
                        </div>
                    </div>{' '}
                    <div className="items-center justify-center text-center bg-gray-50 border-round-lg">
                        <img
                            src={item?.icon}
                            className="w-3rem h-3rem rounded-full"
                            alt="Icon"
                        />
                    </div>
                </div>

                <h6 className="flex align-items-center gap-1 m-0 p-0 mb-2  text-600">
                    <DuoPen /> Description
                </h6>

                <div className="flex flex-wrap  py-2 border-1  border-dashed border-round-lg border-gray-200 p-2 mb-2">
                    {item?.description ? item.description : 'N/a'}{' '}
                </div>
                <h6 className="m-0 p-0 mb-2  text-600">Symptoms</h6>

                <div className="flex flex-wrap  py-2 border-1 border-dashed border-round-lg border-gray-200 p-2 ">
                    {item?.symptoms && item?.symptoms?.length > 0
                        ? item?.symptoms?.map((item, idx) => (
                              <span
                                  className={`text-md text-blue-500 font-bold bg-blue-50  px-2 py-1 border-round mr-1 mb-1`}>
                                  {item}
                              </span>
                          ))
                        : 'No symptoms at the moment.'}
                </div>

                {/* </div> */}
            </div>
        )
    }
    //#endregion

    return (
        <PageContentWrapper>
            <StyledPathwayForm ref={formRef} />

            <StyledCardListWrapper
                title={translateCommons('Pathway Management')}
                description={translateCommons(
                    'Allows you to store and access standardized care pathways. It ensures consistency, improves treatment quality, and supports better patient outcomes.',
                )}
                handleAddNew={handleAddNew}
                initialData={queryData?.content || []}
                className={`min-h-screen `}
                handleAction={handleAction}
                template={template}
                handleOnDelete={handleOnDelete}
            />

            <StyledDeleteDialog ref={deleteRef} onDelete={handleOnDelete} />
        </PageContentWrapper>
    )
}

export default StyledPathwayDataCardList
